package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/state_shared_flow")
@Composable
fun StateSharedFlowPage() {
    BlogLayout(
        PageProperties(
            "State and Shared Flow",
            "Why use StateFlow and SharedFlow, Types of StateFlow. Benefits of StateFlow and SharedFlow. Lifecycle of StateFlow. SharedFlow properties."
        ),
        BlogProperties("Coroutines", "StateFlow | SharedFlow")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Both StateFlow and SharedFlow are Flow types in Kotlin, designed to handle data streams. StateFlow is a hot observable data holder that emits the current and subsequent values to its subscribers.  Unlike traditional LiveData or RxJava observables, StateFlow is built on Kotlin coroutines, making it seamlessly integrate with coroutine-based asynchronous programming. SharedFlow is used to share events across multiple consumers.",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .padding(bottom = 1.em)
            )

            SpanText(
                text = "SharedFlow and StateFlow are built upon the concept of a hot flow. Unlike cold flows, which generate data for each collector, hot flows share emitted values among multiple collectors.",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .padding(bottom = 1.em)
            )

            SpanText(
                text = "Hot flow refers to a type of flow that produces values regardless of whether there are active collectors. cold flows, which only emit values when there is an active collector.",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .padding(bottom = 1.em)
            )

            SpanText(
                text = "Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Think of it as a river carrying data from various sources (sensors, APIs, etc) to different parts of your app.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Consider the marathon you’re running, and each milestone is a data event. As you hit every marker, your smartwatch updates your stats – pace, heart rate, distance. Kotlin Flow acts like a coach for these updates, ensuring you receive data just when you need it. No delays, no missing information – pure synchrony!",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Cold stream - it starts emitting data only when one starts to collect them.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "It does not represent a state",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "To collect from a Flow use the collect function or other coroutine-based operators.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Does not remember past values. Each time you collect from a Flow, it starts from scratch.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "It is immutable once created.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Best for asynchronous data streams where past values aren't crucial.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Essence of Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Sequence of Values: ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    SpanText(
                        text = "Think of a Flow as a flexible conveyor belt delivering a series of values over time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Asynchronous: ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    SpanText(
                        text = "They operate independently of your main thread, perfect for tasks like network responses or user input streams.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Collectors: ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    SpanText(
                        text = "You ‘collect’ values emitted from a Flow as needed. This means your code reacts to incoming data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Collector example",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Most of us have a mobile data subscription plan for our smartphones.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each user (collector) has it’s own mobile data plan (it’s own flow of data) given by a mobile company (provider). Each user consumes and manages this data plan according to their individual needs and one user’s data usage doesn’t affect the others data.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "StateFlow and SharedFlow are both implementations of hot flows that can be used for managing state and sharing data between different parts of the application.",
                modifier = Modifier.padding(bottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "In flows, this means it’s not a single stream of data but multiple copies of data for each collector subscribed to this data from a single provider.",
                modifier = Modifier.padding(bottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Why StateFlow Use",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use StateFlow when managing screen state that changes over time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stateflow enables you to design and develop supervisory control, task scheduling, fault management, communication protocols, user interfaces, and hybrid systems. In Android, StateFlow is a great fit for classes that need to maintain an observable mutable state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "StateFlow - (State-holder observable flow - cold-to-hot conversion aware - Structured concurrency, Multiple collectors, Initial value - Single latest value - UI state - collectAsStateWithLifecycle())",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //StateFlow is a hot, stateful flow that always holds a current value. It behaves like an observable variable — any collector immediately receives the latest value upon subscription.
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For UI State (form fields, screen content, loading state)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Whenever you buy something online, you (collector) are mostly aware of the latest tracking update (state flow) from the package delivery company (producer) that is tracing your order.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Frequently you care about the latest and most recent information and not over past updates, because what usually matters is the current location of your package. Also, many people could be interested in tracking this same order.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In StatusFlow this means that all the collectors observing an StatusFlow will be notified whenever the producer emits a new value and they will receive the latest update when they subscribe.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                SpanText(
                    text = "StateFlow is a part of the Kotlin Flow library to manage and represent a state in an application.",
                    modifier = Modifier.padding(bottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is a type of interface.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Read-only",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StatFlow requires an initial value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow replay behavior: A new collector immediately receives the current state value.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Always returns the updated value. And to receive the updated value we just collect the value from the implemented Flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s hot, meaning it is always active even if there are no observers. StateFlow is a hot observable that stores the latest value. When created, it requires you to pass an initial state to it and any observer can check its current value or subscribe to it at any time to receive updates with the current value as it changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We collect the value from implemented Flow",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A StateFlow is a hot flow that represents a state, holding a single value at a time. It is also a conflated flow, meaning that when a new value is emitted, the most recent value is retained and immediately emitted to new collectors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is useful when you need to maintain a single source of truth for a state and automatically update all the collectors with the latest state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It always has an initial value and only stores the latest emitted value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow is used when one needs to maintain and share a single source of truth for a state and automatically update all collectors with the latest state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Imagine a home screen where the UI can be in one of several distinct states loading, animation, shimmer, displaying content, or showing a delete confirmation dialog. At any point in time, only one state should represent the single source of truth for the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "MutableStateFlow implements Stateflow and overrides the value variable.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "Please note how the value property is overridden inside MutableStateFlow. In Kotlin, an open val property can be overridden with a var property. val only allows getting a value (getter), while var also supports setting a new value (setter).",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    val lapCount: StateFlow<Int> = MutableStateFlow(0)
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UI state: anything the screen needs to display and should survive re-subscriptions — loading state, data models, counters.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It always has a value (requires an initial value), emits only distinct values (no duplicate emissions), and gives new collectors the current value immediately.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    private val _state = mutableStateOf(MarsWeatherState()) // not exposed because mutable
                    val state: State<MarsWeatherState> = _state // expose this to composable because immutable
                    
                    //Syntax: 1
                    private val _stateFlowMessage = MutableStateFlow("") // private mutable state flow
                    val stateFlowMessage = _stateFlowMessage.asStateFlow() // publicly exposed as read-only state flow

                    //Syntax: 2
                    private val _movieViewState: MutableStateFlow<MoviesViewState> = MutableStateFlow(MoviesViewState.Loading)
                    val moviesViewState: StateFlow<MoviesViewState> = _movieViewState.asStateFlow()

                    //Syntax: 3
                    var dogList = mutableStateOf<List<Dog>>(listOf()) private set
                    get() = dogList

                    //Syntax: 4
                    private val _petDetailUiState: MutableStateFlow<PetDetailUiState> = MutableStateFlow(PetDetailUiState.Loading)
                    val petDetailUiState: StateFlow<PetDetailUiState>
                    get() = _petDetailUiState

                    //Syntax: 5
                    private var _getPopularStateFlow = MutableStateFlow<List<CategoriesModel>>(emptyList())
                    var getPopularStateFlow : StateFlow<List<CategoriesModel>> = _getPopularStateFlow
                    """.trimIndent()
            )

            SpanText(
                text = "Managing State in Compose (Declare a MutableState object)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val mutableState = remember { mutableStateOf(default) }
                    
                    var value by remember { mutableStateOf(default) }
                    
                    val (value, setValue) = remember { mutableStateOf(default) }
                    """.trimIndent()
            )

            SpanText(
                text = "Real-World Scenario",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Imagine a classroom with a teacher and students. The teacher writes “Apple” on the board.",
                        modifier = Modifier.padding(topBottom = 0.3.em).fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "All students currently in the classroom see “Apple”. If some students walk in late, they still see “Apple”.",
                        modifier = Modifier.padding(bottom = 0.3.em).fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Now the teacher wipes the board and writes “Mango”.",
                        modifier = Modifier.padding(bottom = 0.3.em).fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "All students now see “Mango”, and students who come in later won’t see “Apple”, only “Mango”.",
                        modifier = Modifier.padding(bottom = 0.3.em).fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Publisher / Producer / Emitter — The teacher",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Data Stream — The words written on the board",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Subscribers / Collectors — Students in the classroom",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State-holder — The whiteboard; StateFlow always holds and emits the latest value",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hot — Publisher (teacher) always emits (writes on the board) whether or not collectors (students) are active (present). New students walking into the classroom (subscribing) immediately see the latest value and subsequent values on the board.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Key Features of StateFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Always holds the latest value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ideal for managing and observing UI state.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Automatically emits the current state to new subscribers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Benefits of StateFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hot Stream",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Always holds the latest state and replays it to new subscribers.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Immutability",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "StateFlow enforces immutability, ensuring that the emitted values cannot be modified once they are emitted.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This helps in writing more predictable and bug-free code.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Scoped Lifecycle",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "StateFlow is scoped to a particular coroutine scope, making it lifecycle-aware.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This means that it automatically cancels subscriptions when the associated coroutine scope is cancelled, preventing potential memory leaks.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Backpressure Handling",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "StateFlow provides built-in support for backpressure handling, allowing subscribers to control the flow of emitted values and prevent overwhelming downstream consumers.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Interoperability",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "StateFlow seamlessly interoperates with other coroutine constructs, such as suspending functions, channels, and flows, making it a versatile tool for asynchronous programming.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Lifecycle of StateFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The lifecycle of a StateFlow in an Android application typically follows the lifecycle of the component that holds it, such as an Activity or Fragment.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The primary purpose of StateFlow is to provide a reactive stream of data, and its behaviour is closely tied to the lifecycle of the component observing it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow is hot, which means if Activity or Fragment navigates or gone through a different configuration even if you are calling flow builder it will execute and returns the latest value to the collector of the state flow which is not the case for flow (which is cold by default).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Creation",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A StateFlow is created and initialized, usually within a ViewModel or another data management component. This typically happens when the associated Android component (e.g., Activity or Fragment) is created.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Observation",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The component that needs to observe the StateFlow subscribes to it, often in the onCreate or onCreateView method of an Android component. This is where you register observers using functions like collect, onEach, or launchIn.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Updating State",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The StateFlow is updated as needed, often in response to user interactions or data changes. One can use functions like value (for mutable StateFlow) or update it within a coroutine.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    KotlinCode(
                        code = """
                            viewModel.updateState("New Value")
                        """.trimIndent()
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Observation Continues",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The observers continue to receive updates from the StateFlow as long as they are active and the StateFlow itself is still in scope.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Lifecycle Events",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "When the Android component (Activity or Fragment) that holds the StateFlow is destroyed or no longer needed, it goes through its lifecycle events, such as onPause, onStop, onDestroy, or onDestroyView. It’s essential to handle the lifecycle events correctly to avoid memory leaks.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Cancelling Observers",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "To prevent resource leaks and unnecessary updates, it’s a good practice to cancel the observation of the StateFlow when the component is no longer active.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The method observe() from LiveData automatically unregisters the consumer when the view enters the STOPPED state. Flow collection is not stopped automatically, but this behaviour can be easily achieved with the repeatOnLifecycle extension.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    KotlinCode(
                        code = """
                             override fun onCleared() {
                                 super.onCleared()
                                 // Cancel all active coroutines, including StateFlow    observations
                                 viewModelScope.cancel()
                             }
                        """.trimIndent()
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Cleanup",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Any cleanup tasks related to the StateFlow can be performed in the appropriate lifecycle event (e.g., onDestroy or onDestroyView) of the Android component.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Properly manage resources",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "When using a SharedFlow or StateFlow, ensure that you properly manage resources by canceling coroutines or collectors when they are no longer needed.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    KotlinCode(
                        code = """
                             val scope = CoroutineScope(Dispatchers.Main)

                             val sharedFlow = MutableSharedFlow<Int>()

                             //var uiState by mutableStateOf(DetailScreenState())

                             val job = scope.launch {
                                 sharedFlow.collect { value ->
                                     println(value)
                                 }
                             }

                             // Later, when the collector is no longer needed
                             job.cancel()
                        """.trimIndent()
                    )
                }
            }

            SpanText(
                text = "Backpressure Handling in StateFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow provides backpressure handling support by using a buffered approach. When the collector is unable to keep up with the rate of emissions, StateFlow buffers emitted values and emits them to the collector when it's ready to consume.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This buffering mechanism helps prevent resource exhaustion and ensures smooth communication between producers and consumers in reactive applications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Whenever the value is accessed, the value from all StateFlows is calculated, which can be problematic if heavy computation is happening while calculation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow collect emit NullPointerException.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateIn operator doesn’t update the cached value of StateFlow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Whenever the value is accessed, the value from all StateFlows is calculated, which can be problematic if heavy computation is happening while calculation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "StateFlow Notes",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow collect emit NullPointerException.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateIn operator doesn’t update the cached value of StateFlow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Whenever the value is accessed, the value from all StateFlows is calculated, which can be problematic if heavy computation is happening while calculation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What makes StateFlow different from Flow?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Management: StateFlow holds a single latest value that is accessible at any time using .value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Overwriting Values: Multiple emissions may be overridden if a new value is emitted before the previous one is collected.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Initial Value: StateFlow requires an initial value, unlike regular Flows.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "SharedFlow - (event-based flow with no persistent state)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each person (collector) who tunes in a particular TV channel (producer), starts watching a live program (shared flow of data) from the exact moment they joined. This implies that people could miss the beginning of the transmission if they tuned the live program late. In this case it is a single program transmitted to multiple viewers and everyone is watching the same content.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SharedFlow is used for one-time events like showing a toast message, navigating between screens, or triggering actions in response to user interaction.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SharedFlow is a type of Flow that allows multiple collectors to receive the same emitted values. It's an implementation of a hot stream, meaning it stays active regardless of whether it has active subscribers. Unlike StateFlow, which retains the last emitted value and replays it for any new collector, SharedFlow can be configured to replay multiple recent values or none at all.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SharedFlow this means that it is a single stream of data for all the collectors and late subscribers may lose the previous emissions until the point they joined the flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A SharedFlow is a hot flow that can have multiple collectors. It can emit values independently of the collectors, and multiple collectors can collect the same values from the flow. (It’s hot, meaning it doesn’t depend on collectors to emit events)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SharedFlow is a hot flow that can broadcast values to multiple collectors without retaining the latest value. It is best suited for handling events and one-time signals.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SharedFlow in contrast to a normal Flow is hot, every collector uses the same SharedFlow, because it is shared.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s useful when you need to broadcast a value to multiple collectors or when you want to have multiple subscribers to the same stream of data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutableSharedFlow has 2 emission functions, tryEmit and emit respectively.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                SpanText(
                    text = "The mutable state holder flow emits the current and initial value to it’s collector.",
                    modifier = Modifier.padding(bottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                Ul(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "replay: How much the new subscriber who called collect() will get the latest released items. The replay parameter in a SharedFlow specifies how many of the most recent emissions are buffered and re-emitted to new collectors. It’s ideal for cases where you want late joiners to access a set number of recent values without missing out. It defaults to replay = 0, meaning no values are retained. As a result, new collectors will not receive any past emissions.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "extraBufferCapacity: How many additional elements will be saved for a “slow” subscriber who does not have time to execute collect()",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "onBufferOverflow: defines what to do when emit() is called when extraBuffer is full. SUSPEND (default), DROP_OLDEST, DROP_LATEST",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    sealed class LoginEvent {
                        data object NavigateToHome : LoginEvent()
                        data class ShowError(val message: String) : LoginEvent()
                    }
                    
                    class LoginViewModel : ViewModel() {
                        private val _events = MutableSharedFlow<LoginEvent>(
                            replay = 0
                        )
                        val events: SharedFlow<LoginEvent> = _events
                        fun loginSuccess() {
                            viewModelScope.launch {
                                _events.emit(LoginEvent.NavigateToHome)
                            }
                        }
                        fun loginFailed() {
                            viewModelScope.launch {
                                _events.emit(LoginEvent.ShowError("Invalid credentials"))
                            }
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                SpanText(
                    text = "The reason for this is the way these two functions work:",
                    modifier = Modifier.padding(bottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The tryEmit operates using buffers. So it tries to emit a new value to all the subscribers when there is buffer space available. When using the default constructor of MutableSharedFlow the extraBufferCapacity parameter always defaults to 0.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The emit is a suspending function, means when there is a value that is emitted but the subscriber has not yet consumed it, it suspends block further emissions into the MutableSharedFlow and hence it can work without a buffer value.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SharedFlow as a hot flow will emit data even if no one listening. Channel will hold data till someone consumes it. So if your view is not ready to receive an event and sharedFlow emits it, the event will be lost. So channels are better to send one-time events. Obviously, you can set reply count for shared flow, but then your event will be repeated.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SharedFlow has its buffer called replay cache. It keeps a specific number of the most recent values in it. Every new subscriber gets the values from the replay cache and then gets new emitted values. You can set the maximum size of the replay cache in replay parameter in the constructor. A replay cache also provides buffer for emissions to the shared flow, allowing slow subscribers to get values from the buffer without suspending emitters. A SharedFlow with a buffer can be configured to avoid suspension of emitters on buffer overflow using the onBufferOverflow parameter, which is equal to one of the entries of the BufferOverflow enum. When a strategy other than SUSPENDED is configured, emissions to the shared flow never suspend.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If you use the default SharedFlow constructor of MutableSharedFlow then the replay cache won’t be created.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It does not have an initial value, and you can configure its replay cache to store a certain number of previously emitted values for new collectors.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "state Flow will update the latest when configuration changes for Activity or Fragments, but when we want to show the snack bar once only to indicate the value is being fetched from the server, if we use State Flow when a user rotates the screen, Activity or Fragments will be created resulting into value again will be fetched from server and snack bar will appear more than once, to avoid this we use Shared Flow.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                SpanText(
                    text = "Use buffer and replay",
                    modifier = Modifier.padding(bottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If you declare a MutableSharedFlow without any extraBufferCapacity parameter set to greater than 0, the tryEmit function will never succeed and you will always receive a false value.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "But If you use emit then it will work properly.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "For SharedFlow, you can set the buffer capacity and replay capacity. Choose a suitable buffer capacity to avoid backpressure issues, and set the replay capacity according to the requirements of your use case.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "For StateFlow, keep in mind that it always has a replay cache of size 1, meaning it retains the latest value for new collectors.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    KotlinCode(
                        code = """
                            val testFlow = MutableSharedFlow<String>(replay = 1, extraBufferCapacity = 1)
                        """.trimIndent()
                    )

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "replay = 0 means that once an update from the flow has been collected, it will never be seen again (e.g when rotating the device).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Changing the replay to 1 will sort of turn this into a StateFlow, as the last known value will be emitted to anyone starting a collection. Increasing that number further will also increase the number of updates that any “new” collection gets respectively.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    // Data classes
                    data class ChatId(val value: String) 
                    data class Message(val chatId: ChatId, val text: String, val timestamp: Long = System.currentTimeMillis())

                    // SharedFlow with error handling and ChatId
                    private val _chatMessages = MutableSharedFlow<Result<Message>>(extraBufferCapacity = 1) 
                    val chatMessages: SharedFlow<Result<Message>> = _chatMessages.asSharedFlow()

                    // Function to receive messages
                    fun onNewMessageReceived(chatId: ChatId, message: Message) {
                        viewModelScope.launch {
                            try {
                                _chatMessages.emit(Result.success(message))
                            } catch (e: Exception) {
                                _chatMessages.emit(Result.failure(e))
                            }
                        }
                    }

                    // RecyclerView Adapter
                    // ... similar adapter structure, but add error handling

                    init { 
                        // Collect messages from SharedFlow
                        viewModel.chatMessages.collect { result ->
                            result.fold(
                                onSuccess = { message -> 
                                    // Filter for current chat if chatId is specified
                                    if(currentChatId == message.chatId) { 
                                        messages.add(message)
                                        notifyItemInserted(messages.lastIndex)
                                    }
                                },
                                onFailure = { error ->
                                    // Handle error appropriately (e.g., display on UI)
                                }
                            )
                        }
                    }

                    // ViewModel
                    class ChatViewModel(private val chatId: ChatId? = null) : ViewModel() {

                        // ... other ViewModel logic related to fetching messages

                        // Filter messages if a chatId is provided
                        // The shareIn call in the ViewModel ensures efficient stream usage and provides a replay of the last value, which is useful if a new ChatActivity starts for that chat.
                        val chatMessages: SharedFlow<Result<Message>> = chatMessages
                            .filter { if (chatId != null) it.getOrNull()?.chatId == chatId else true }
                            .shareIn(viewModelScope, SharingStarted.WhileSubscribed(), replay = 1)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Key Features of SharedFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "Hot Stream — Active even if there are no collectors.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "Supports multiple collectors simultaneously.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "Supports Buffering — Can hold multiple values, preventing data loss due to slow collectors.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "Does not retain the latest value unless configured with a replay buffer.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "Used for event streams.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does SharedFlow work?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SharedFlow emits values continuously, and new collectors receive values based on the replay count:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

               Li {
                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "If a collector subscribes before emissions, it receives all values as they are emitted, regardless of the replay setting.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "If a collector subscribes after emissions, it receives only the last N values based on the replay count.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }
               }
            }

            SpanText(
                text = "When to Use SharedFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use SharedFlow when emitting events that shouldn’t persist. one-time events: navigation, error toasts, dialogs — anything that should fire once and not replay on re-subscription.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "one-time events",
                        modifier = Modifier.padding(bottom = 0.3.em).fontSize(FontSize.Medium)
                    )

                    Ol {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Navigation to another screen",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Success/Error toasts - Payment, Login",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Push Notification Event",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Dialogs — anything that should fire once and not replay on re-subscription.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "Difference between “replay” and “extraBufferCapacity” in Kotlin MutableSharedFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "replay and extraBufferCapacity seems doing the same job, which is to provide a buffer space for the SharedFlow to emit value (and it’s extremely important if you use tryEmit when emitting value, SharedFlow buffer space is defaulted to 0, so you won’t get anything if replay or extraBufferCapacity are not specified.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "replay — the number of values replayed to new subscribers (cannot be negative, defaults to zero).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "extraBufferCapacity — the number of values buffered in addition to replay. emit does not suspend while there is a buffer space remaining (optional, cannot be negative, defaults to zero).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "SharedFlow vs. StateFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                SpanText(
                    text = "SharedFlow",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s like a stream of water (data) with a current state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Broadcasts emitted values to multiple collectors. Think of it like a live TV broadcast. If you “tune in” late, you miss the early content.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You can configure options around replay and an initial value, but it’s not required.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SharedFlow allows multiple collectors and can buffer and replay values for new subscribers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                SpanText(
                    text = "StateFlow",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s like a shared water source that multiple people can access and get water (data) from.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Always holds the most recent value. New collectors receive the last emitted item immediately, like a DVR playback starting from the current point.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Must have an initial value.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(src = Resources.Images.STATE_FLOW_VS_SHARED_FLOW,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "StateFlow Vs. SharedFlow")

            SpanText(
                text = "When to use which",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SharedFlow",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Event Streams: User clicks, one-time notifications, where missing past events is acceptable.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Multicasting: When multiple parts of your app need to react to the same data stream simultaneously.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Representing UI State: Perfectly suits holding the current state of your screen (loading, data, error), ensuring new UI elements start with the latest state.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "StateFlow ensures your UI always reflects the current loading, success, or error state.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Combining Coroutines with SharedFlow and StateFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.flow.*

                    class AppViewModel {
                        private val _state = MutableStateFlow("Idle")
                        val state: StateFlow<String> = _state

                        private val _events = MutableSharedFlow<String>()
                        val events: SharedFlow<String> = _events.asSharedFlow()

                        fun updateState(newState: String) {
                            _state.value = newState
                        }

                        suspend fun sendEvent(event: String) {
                            _events.emit(event)
                        }
                    }

                    fun main() = runBlocking {
                        val viewModel = AppViewModel()

                        // Collect state changes
                        launch {
                            viewModel.state.collect { state ->
                                println("State updated to: $state")
                            }
                        }

                        // Collect events
                        launch {
                            viewModel.events.collect { event ->
                                println("Event received: $event")
                            }
                        }

                        // Simulate updates
                        viewModel.updateState("Loading")
                        viewModel.sendEvent("UserLoggedIn")
                        viewModel.updateState("Complete")
                        viewModel.sendEvent("DataRefreshed")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Interview Question",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    public interface StateFlow<out T> : SharedFlow<T> {
                        public val value: T
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MutableStateFlow and MutableSharedFlow have completely different implementations. MutableStateFlow is not built on top of MutableSharedFlow... although... both StateFlowlmpl and SharedFlowlmpl extend AbstractSharedFlow and implement many common interfaces.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}

/*
emit(): The emit function allows the ViewModel to send a new event into the SharedFlow. In this example, it sends a String message.

collect(): In the Activity, we use collect within repeatOnLifecycle to observe eventFlow. repeatOnLifecycle ensures that the collection is paused and resumed according to the Activity’s lifecycle, conserving resources by suspending collection when the Activity is not visible.
*/